const DRAFT_KEY_PREFIX = "finster_addin_composer_draft";

function storageKey(host: string, scopeKey: string): string {
  return `${DRAFT_KEY_PREFIX}:${host}:${scopeKey}`;
}

function defaultStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Reads a taskpane composer draft for one host and document/chat scope.
 *
 * @param host - Office host identifier, such as `excel` or `powerpoint`.
 * @param scopeKey - Account, document, and chat identity for the draft.
 * @param storage - Optional storage implementation, primarily for tests.
 * @returns The saved draft, or an empty string when unavailable.
 * @throws Never; inaccessible browser storage is treated as empty.
 */
export function readComposerDraft(
  host: string,
  scopeKey: string,
  storage: Storage | null = defaultStorage()
): string {
  if (!storage || !scopeKey) return "";
  try {
    return storage.getItem(storageKey(host, scopeKey)) ?? "";
  } catch {
    return "";
  }
}

/**
 * Saves a taskpane composer draft for one host and document/chat scope.
 *
 * @param host - Office host identifier, such as `excel` or `powerpoint`.
 * @param scopeKey - Account, document, and chat identity for the draft.
 * @param value - Current composer text; an empty value removes the entry.
 * @param storage - Optional storage implementation, primarily for tests.
 * @returns Nothing.
 * @throws Never; inaccessible browser storage is ignored.
 * @sideEffects Writes or removes one `sessionStorage` entry.
 */
export function saveComposerDraft(
  host: string,
  scopeKey: string,
  value: string,
  storage: Storage | null = defaultStorage()
): void {
  if (!storage || !scopeKey) return;
  try {
    if (value) storage.setItem(storageKey(host, scopeKey), value);
    else storage.removeItem(storageKey(host, scopeKey));
  } catch {
    // Draft persistence is best-effort and must never block submission.
  }
}

/**
 * Removes a saved taskpane composer draft.
 *
 * @param host - Office host identifier, such as `excel` or `powerpoint`.
 * @param scopeKey - Account, document, and chat identity for the draft.
 * @param storage - Optional storage implementation, primarily for tests.
 * @returns Nothing.
 * @throws Never; inaccessible browser storage is ignored.
 * @sideEffects Removes one `sessionStorage` entry.
 */
export function clearComposerDraft(
  host: string,
  scopeKey: string,
  storage: Storage | null = defaultStorage()
): void {
  saveComposerDraft(host, scopeKey, "", storage);
}
